/**
 * Referred revenue and commission, one row per currency (TMMPDM-0042). Never summed.
 * Weekly sign-ups are a table (the text alternative) with a bar beside each count.
 * The bar scales to that table's peak. There is no ceiling.
 */
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState } from '@/components/page';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { formatDate } from '@/format';
import type {
  AffiliatePerformance,
  AffiliateReferralsSummary,
  WeeklySignups,
} from '@/api/tmm/types';

interface AffiliatePerformanceTableProps {
  performance: AffiliatePerformance[];
  referrals: AffiliateReferralsSummary;
  weeklySignups: WeeklySignups[];
}

export function AffiliatePerformanceTable({
  performance,
  referrals,
  weeklySignups,
}: AffiliatePerformanceTableProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const maxSignups = weeklySignups.reduce((max, row) => (row.signups > max ? row.signups : max), 0);

  return (
    <Stack spacing={2}>
      <Typography variant="body1">
        {t('affiliates.performance.summary', '{{active}} of {{count}} referred subscribers', {
          active: referrals.active,
          count: referrals.total,
        })}
      </Typography>
      {performance.length === 0 ? (
        <EmptyState surface title={t('affiliates.performance.empty', 'No referred revenue yet.')} />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
          <Table
            size="small"
            stickyHeader
            aria-label={t(
              'affiliates.performance.aria',
              'Referred revenue and commission by currency',
            )}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('affiliates.col.currency', 'Currency')}</TableCell>
                <TableCell align="right">
                  {t('affiliates.col.revenue30', 'Referred revenue, 30 days')}
                </TableCell>
                <TableCell align="right">
                  {t('affiliates.col.revenueToDate', 'Referred revenue, to date')}
                </TableCell>
                <TableCell align="right">
                  {t('affiliates.col.commission30', 'Commission, 30 days')}
                </TableCell>
                <TableCell align="right">
                  {t('affiliates.col.commissionToDate', 'Commission, to date')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {performance.map((row) => (
                <TableRow key={row.currency} hover>
                  <TableCell>{row.currency}</TableCell>
                  <TableCell align="right">
                    <Amount minor={row.referredRevenue30dMinor} currency={row.currency} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.referredRevenueToDateMinor} currency={row.currency} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.commission30dMinor} currency={row.currency} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.commissionToDateMinor} currency={row.currency} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="h3" component="h3">
        {t('affiliates.signups.title', 'Weekly sign-ups')}
      </Typography>
      {weeklySignups.length === 0 ? (
        <EmptyState surface title={t('affiliates.signups.empty', 'No weekly sign-ups yet.')} />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
          <Table size="small" aria-label={t('affiliates.signups.aria', 'Weekly sign-ups')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('affiliates.col.week', 'Week')}</TableCell>
                <TableCell align="right">{t('affiliates.col.signups', 'Sign-ups')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklySignups.map((row) => (
                <TableRow key={row.weekStart}>
                  <TableCell>{formatDate(row.weekStart, locale)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          flex: '1 1 auto',
                          minWidth: 0,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Box
                          aria-hidden="true"
                          sx={{
                            height: 8,
                            width:
                              maxSignups === 0 || row.signups === 0
                                ? 0
                                : `${Math.round((row.signups / maxSignups) * 100)}%`,
                            bgcolor: 'primary.main',
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                      <Amount count={row.signups} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
